// Client del motor de reportes del data lake. Sin base URL configurada no hace ninguna petición:
// caer al base URL por defecto le pegaría a otro API.
import { apiFetch } from '../../client';
import type {
    AnalyticsApi,
    CatalogColumnWire,
    CatalogTablesWire,
    CatalogTableWire,
    JobCsvWire,
    ReportJobsWire,
    ReportJobWire,
    ReportRequest,
    RunReportWire,
    TableColumnsWire,
} from './analyticsContract';

/** El servicio de reportes no tiene base URL en este ambiente. */
export class ReportsServiceNotConfiguredError extends Error {
    constructor() {
        super('El servicio de reportes no está configurado en este ambiente.');
        this.name = 'ReportsServiceNotConfiguredError';
    }
}

/** Base URL del motor, leída en cada llamada; `null` si falta o viene vacía. */
function configuredBaseUrl(): string | null {
    const value = import.meta.env.VITE_API_DATALAKE_QUERY_BASE_URL as string | undefined;
    return value?.trim() ? value : null;
}

/** `true` si el ambiente declara la base URL del motor. */
export function isAnalyticsConfigured(): boolean {
    return configuredBaseUrl() !== null;
}

/** Hace la petición solo si hay base URL; si no, rechaza sin tocar la red. */
function analyticsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    const baseUrl = configuredBaseUrl();
    if (!baseUrl) return Promise.reject(new ReportsServiceNotConfiguredError());
    return apiFetch<T>(path, { ...init, baseUrl });
}

export const analyticsClient: AnalyticsApi = {
    listCatalog: async (): Promise<CatalogTableWire[]> =>
        (await analyticsFetch<CatalogTablesWire>('/backoffice/analytics/catalog')).tables,

    describeTable: async (qualifiedName: string): Promise<CatalogColumnWire[]> =>
        (
            await analyticsFetch<TableColumnsWire>(
                `/backoffice/analytics/catalog/${encodeURIComponent(qualifiedName)}`,
            )
        ).columns,

    runReport: (request: ReportRequest): Promise<RunReportWire> =>
        analyticsFetch<RunReportWire>('/backoffice/analytics/run', {
            method: 'POST',
            body: JSON.stringify(request),
        }),

    listJobs: async (): Promise<ReportJobWire[]> =>
        (await analyticsFetch<ReportJobsWire>('/backoffice/analytics/jobs')).jobs,

    getJob: (jobId: string): Promise<ReportJobWire> =>
        analyticsFetch<ReportJobWire>(`/backoffice/analytics/jobs/${encodeURIComponent(jobId)}`),

    getJobCsv: (jobId: string): Promise<JobCsvWire> =>
        analyticsFetch<JobCsvWire>(`/backoffice/analytics/jobs/${encodeURIComponent(jobId)}/csv`),
};
